import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Clock3, ShieldCheck, Mail } from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";

export default function PendingApproval() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();

  if (!user) return <Navigate to="/login" replace />;
  if (user.account_status === "APPROVED" || user.role === "SUPER_ADMIN") {
    return <Navigate to={user.role === "BUYER" ? "/buyer" : "/vendor"} replace />;
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center px-6">
      <div className="absolute -top-24 -left-24 w-96 h-96 ceramic-orb opacity-30" />
      <div className="absolute bottom-10 right-10 w-72 h-72 ceramic-cube opacity-30" />

      <div className="relative max-w-xl w-full">
        <Logo size={48} className="mb-8" />
        <div className="surface-card p-10" data-testid="pending-approval-screen">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-100 border border-yellow-200 text-yellow-900 text-xs font-semibold uppercase tracking-wider">
            <Clock3 className="w-3 h-3" /> {user.account_status}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black mt-6">Pending Super Admin approval</h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Thanks for registering on <span className="text-foreground font-semibold">tilesphere</span>, {user.full_name}.
            Your account &amp; tenant organization are now in review by the Projexino HQ team.
            You'll get access to the full portal once approved.
          </p>

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <div className="font-semibold">Why approval is required</div>
                <div className="text-sm text-muted-foreground">tilesphere is a vetted B2B platform. Every vendor and buyer is verified before they can transact.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <div className="font-semibold">Need to expedite?</div>
                <div className="text-sm text-muted-foreground">Email <span className="text-foreground font-mono">hq@projexino.com</span> with your organization details.</div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-10">
            <Button onClick={async () => { await refresh(); }} data-testid="refresh-status-btn">Refresh status</Button>
            <Button variant="outline" onClick={async () => { const p = await logout(); navigate(p || "/login"); }} data-testid="signout-pending-btn">Sign out</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
